import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useCreativeProject } from "../../hooks/creative/useCreativeProjects";
import { ProjectCarousel } from "./ProjectCarousel";
import { getCloudFrontImageUrl } from "../../util/cloudFrontUtils";
import awsConfig from "../../config/aws-config";
import classes from "./ProjectOverview.module.css";

export const ProjectOverview = ({ creative, loading: creativeLoading, error: creativeError }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: creativeId, projectId } = useParams();
  const { project, loading: projectLoading, error: projectError } = useCreativeProject(projectId);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselStartIndex, setCarouselStartIndex] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);
  const [horizontalOffset, setHorizontalOffset] = useState(0);
  const carouselRef = useRef(null);

  // Get current user from Redux
  const { user: currentUser } = useSelector((state) => state.auth || {});
  const currentUserId = currentUser?._id || currentUser?.id;
  
  // Check if current user is the owner of the creative
  const isOwner = currentUserId && creative?.userID && currentUserId === creative.userID;
  
  // Check if we're coming from creative-editing route (for back navigation)
  const isFromCreativeEditing = location.pathname.includes('/creative-editing/');
  
  // Check if we're on a member page route (hide edit buttons on member pages)
  const isMemberPage = location.pathname.startsWith('/members/');

  const loading = creativeLoading || projectLoading;
  const error = creativeError || projectError;

  // Helper function to get media items
  const getMediaItems = useCallback(() => {
    if (!project?.processedImages || project.processedImages.length === 0) {
      if (!project?.images || project.images.length === 0) return [];
      
      return project.images.map((item, index) => {
        // Check if it's a link (YouTube/Vimeo)
        const isLink = item.type === 'link' || 
          (item.url && typeof item.url === 'string' && 
           (item.url.includes('youtube.com') || item.url.includes('youtu.be') || item.url.includes('vimeo.com')));
        
        // Check if it's a video - check type first, then file extension
        const isVideo = item.type === 'video' || 
          (item.url && typeof item.url === 'string' && 
           /\.(mp4|mov|avi|wmv|flv|webm|mkv|m4v)$/i.test(item.url));
        
        // For links, use thumbnailUrl for display, keep original url for the link
        // For videos, use thumbnailUrl if available, otherwise construct from url
        // For photos, use url
        let displayUrl = '';
        let videoUrl = null;
        
        if (isLink) {
          displayUrl = item.thumbnailUrl || '';
        } else if (isVideo) {
          // Video file URL - construct S3 URL
          if (item.url && !item.url.startsWith('http')) {
            const cleanKey = item.url.startsWith('public/') ? item.url : `public/${item.url}`;
            videoUrl = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
          } else {
            videoUrl = item.url;
          }
          
          // Use thumbnail for display - construct CloudFront URL if thumbnailUrl is a key
          if (item.thumbnailUrl) {
            if (item.thumbnailUrl.startsWith('blob:') || item.thumbnailUrl.startsWith('http')) {
              displayUrl = item.thumbnailUrl;
            } else {
              // Try to construct CloudFront URL
              try {
                displayUrl = getCloudFrontImageUrl(item.thumbnailUrl, {
                  width: 500,
                  height: 500,
                  fit: 'cover'
                }) || item.thumbnailUrl;
              } catch {
                displayUrl = item.thumbnailUrl;
              }
            }
          } else {
            displayUrl = '';
          }
        } else {
          displayUrl = item.url || '';
        }
        
        return {
          ...item,
          displayUrl,
          thumbnailUrl: item.thumbnailUrl || (isLink ? '' : undefined),
          videoUrl,
          isLink,
          isVideo,
          isPhoto: !isLink && !isVideo
        };
      });
    }
    
    return project.processedImages.map((img, index) => {
      if (typeof img === 'string') {
        return {
          displayUrl: img,
          isPhoto: true,
          isLink: false,
          isVideo: false
        };
      }
      
      const originalImage = project.images?.[index];
      
      // Check if it's a link (YouTube/Vimeo) - check both originalImage and img
      const urlToCheck = originalImage?.url || img.url || '';
      const isLink = originalImage?.type === 'link' || 
        (urlToCheck && typeof urlToCheck === 'string' && 
         (urlToCheck.includes('youtube.com') || urlToCheck.includes('youtu.be') || urlToCheck.includes('vimeo.com')));
      
      // Check if it's a video - check type first, then file extension from either source
      const isVideo = originalImage?.type === 'video' || 
        (urlToCheck && typeof urlToCheck === 'string' && 
         /\.(mp4|mov|avi|wmv|flv|webm|mkv|m4v)$/i.test(urlToCheck));
      
      // For videos and links, use processedThumbnailUrl if available, otherwise fallback
      let displayUrl = '';
      let videoUrl = null;
      
      if (isLink) {
        // For links (YouTube/Vimeo), use thumbnailUrl directly from originalImage
        // YouTube/Vimeo thumbnails are already full URLs and don't need processing
        displayUrl = originalImage?.thumbnailUrl || img.thumbnailUrl || img.processedThumbnailUrl || '';
      } else if (isVideo) {
        // For videos, prioritize processedThumbnailUrl for display
        displayUrl = img.processedThumbnailUrl || img.thumbnailUrl || originalImage?.thumbnailUrl || '';
        
        // Video file URL - construct S3 URL (will be resolved properly in VideoPlayer using getUrl)
        if (originalImage?.url) {
          const videoKey = originalImage.url;
          if (videoKey && !videoKey.startsWith('http')) {
            const cleanKey = videoKey.startsWith('public/') ? videoKey : `public/${videoKey}`;
            videoUrl = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
          } else {
            videoUrl = videoKey;
          }
        }
      } else {
        // For photos, use processedUrl
        displayUrl = img.processedUrl || img.url || originalImage?.url || '';
      }
      
      return {
        ...originalImage,
        displayUrl,
        // For links, prioritize original thumbnailUrl (YouTube/Vimeo URLs)
        // For videos, use processedThumbnailUrl if available
        thumbnailUrl: isLink 
          ? (originalImage?.thumbnailUrl || img.thumbnailUrl || img.processedThumbnailUrl)
          : (img.processedThumbnailUrl || img.thumbnailUrl || originalImage?.thumbnailUrl),
        processedThumbnailUrl: img.processedThumbnailUrl, // Pass through for VideoPlayer component
        processedUrl: img.processedUrl, // Pass through for reference
        videoUrl,
        isPhoto: !isLink && !isVideo,
        isLink,
        isVideo
      };
    }).filter(item => item.displayUrl || item.isLink || item.isVideo);
  }, [project]);

  const handleBack = () => {
    // if (creativeId) {
    //   navigate(`/creative-editing/${creativeId}`);
    // } else {
      navigate(-1);
    // }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleGalleryItemClick = (index) => {
    setCarouselStartIndex(index);
    setCarouselOpen(true);
  };

  const handleCarouselClose = () => {
    setCarouselOpen(false);
  };

  const handleMouseDown = (e) => {
    if (!mediaItems?.length) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragOffset(0);
    setHasDragged(false);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !mediaItems?.length) return;
    const currentX = e.clientX;
    const diff = currentX - dragStartX;
    const container = carouselRef.current;
    const containerWidth = container ? container.offsetWidth : 1;
    const offsetPercent = (diff / containerWidth) * 100;
    // Hard block edge drag beyond bounds
    let constrained = offsetPercent;
    if (currentImageIndex === 0 && offsetPercent > 0) constrained = 0;
    if (currentImageIndex === mediaItems?.length - 1 && offsetPercent < 0) constrained = 0;
    setDragOffset(constrained);
    // Mark as dragged if movement is significant
    if (Math.abs(constrained) > 2) {
      setHasDragged(true);
    }
  };

  const commitDrag = () => {
    if (!isDragging || !mediaItems?.length) return;
    const threshold = 5; // percent
    const dragDistance = Math.abs(dragOffset);
    if (dragDistance > threshold) {
      if (dragOffset > 0 && currentImageIndex > 0) {
        setCurrentImageIndex(currentImageIndex - 1);
      } else if (dragOffset < 0 && currentImageIndex < mediaItems?.length - 1) {
        setCurrentImageIndex(currentImageIndex + 1);
      }
    }
    setIsDragging(false);
    setDragOffset(0);
    // Reset hasDragged after a short delay to allow click handler to check it
    setTimeout(() => setHasDragged(false), 100);
  };

  const handleMouseUp = () => commitDrag();
  const handleMouseLeave = () => { if (isDragging) commitDrag(); };

  const handleTouchStart = (e) => {
    if (!mediaItems?.length) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStartX(touch.clientX);
    setDragOffset(0);
    setHasDragged(false);
  };

  const translateX = (() => {
    const base = -100 * currentImageIndex;
    return isDragging ? base + dragOffset : base;
  })();

  const handleTouchMove = (e) => {
    
    if (!isDragging) return;
    // Prevent vertical page scroll while swiping carousel
    if (e.cancelable) e.preventDefault();
    const touch = e.touches[0];
    const currentX = touch.clientX;
    const diff = currentX - dragStartX;
    const container = carouselRef.current;
    const containerWidth = container ? container.offsetWidth : 1;
    const offsetPercent = (diff / containerWidth) * 100;
    let constrained = offsetPercent;
    if (currentImageIndex === 0 && offsetPercent > 0) constrained = 0; // block right swipe on first image
    if (currentImageIndex === mediaItems?.length - 1 && offsetPercent < 0) constrained = 0; // block left swipe on last image
    setDragOffset(constrained);
    // Mark as dragged if movement is significant
    if (Math.abs(constrained) > 2) {
      setHasDragged(true);
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  const handleTouchEnd = () => commitDrag();

  useEffect(() => {
    const syncHorizontalOffset = () => {
      setHorizontalOffset(window.scrollX || window.pageXOffset || 0);
    };

    syncHorizontalOffset();
    window.addEventListener("scroll", syncHorizontalOffset, { passive: true });
    window.addEventListener("resize", syncHorizontalOffset);

    return () => {
      window.removeEventListener("scroll", syncHorizontalOffset);
      window.removeEventListener("resize", syncHorizontalOffset);
    };
  }, []);

  if (loading) {
    return (
      <div className={classes.container}>
        <header className={classes.header}>
          <div className={classes.skeletonBackButton}>
            <div className={classes.skeletonIcon}></div>
            <div className={classes.skeletonText} style={{ width: '120px' }}></div>
          </div>
          <div className={classes.skeletonTitle}></div>
          <div className={classes.skeletonEditButton}></div>
        </header>

        <div className={classes.content}>
          <div className={classes.descriptionSection}>
            <div className={classes.skeletonHeading} style={{ width: '200px' }}></div>
            <div className={classes.skeletonText} style={{ width: '100%' }}></div>
            <div className={classes.skeletonText} style={{ width: '100%' }}></div>
            <div className={classes.skeletonText} style={{ width: '80%' }}></div>
          </div>

          <div className={classes.gallerySection}>
            <div className={classes.galleryGrid}>
              {[...Array(6)].map((_, index) => (
                <div key={index} className={classes.skeletonGalleryItem}></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className={classes.container}>
        <p>Projekt wurde nicht gefunden.</p>
        <button onClick={handleBack}>Zurück</button>
      </div>
    );
  }

  const mediaItems = getMediaItems();
  const fixedPhoneFooterStyle = {
    transform: `translateX(-${horizontalOffset}px)`,
    minWidth: "335px",
    width: "max(100vw, 335px)"
  };

  return (
    <div className={classes.container}>
      <header  className={classes.header}>
        <button className={classes.backButton} onClick={handleBack}>
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="20" viewBox="0 0 11 20" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M0 9.6255L8.99855 0.626953L10.2006 1.82903L2.35773 9.67193L10.2486 18.2204L8.99941 19.3734L0.0172915 9.64279L0 9.6255Z" fill="#1F1F1F"/>
          </svg>
          <span className="mediumLarge">Alle Arbeiten</span>
        </button>
        <h1 style={{ position: isMemberPage ? 'absolute' : 'relative', left: isMemberPage ? '50%' : 'auto', transform: isMemberPage ? 'translateX(-50%)' : 'none' }} className={`${classes.title} semiboldLarge`}>{project.title || 'Projekt'}</h1>
        {!isMemberPage && isOwner && creativeId && projectId && project && (
          <Link 
            to={`/creative-editing/${creativeId}/project/${projectId}`}
            className={`${classes.editButton} mediumLarge`}
          >
            Bearbeiten
          </Link>
        )}
      </header>

      <div className={classes.content}>
        {project.description && (
          <div onClick={toggleExpand} className={classes.descriptionSection}>
            <h2 className="semiboldLarge">Projektbeschreibung</h2>
            <p 
              className={`regularLarge ${!isExpanded ? classes.clamp3 : ''}`}
            >
              {project.description}
            </p>
            {project.description && project.description.length > 150 && (
              <button className={`semiboldMiddle ${isExpanded ? classes.isExpanded : ''}`} onClick={toggleExpand}>
                {isExpanded ? 'Weniger anzeigen' : 'Weiterlesen'}
              </button>
            )}
          </div>
        )}
        {mediaItems?.length > 0 && (
          <div className={classes.gallerySection}>
            <div className={classes.galleryGrid}>
              {mediaItems?.map((item, index) => (
                <div 
                  key={index}
                  className={classes.galleryItem}
                  onClick={() => handleGalleryItemClick(index)}
                >
                  {item.isLink && (
                    <div className={classes.linkBadge}>
                      <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="40" cy="40" r="40" fill="rgba(0, 0, 0, 0.6)"/>
                        <path d="M32 24L56 40L32 56V24Z" fill="white"/>
                      </svg>
                    </div>
                  )}
                  {item.isVideo && !item.isLink && (
                    <div className={classes.videoBadge}>
                      <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="40" cy="40" r="40" fill="rgba(0, 0, 0, 0.6)"/>
                        <path d="M32 24L56 40L32 56V24Z" fill="white"/>
                      </svg>
                    </div>
                  )}
                  {item.isLink ? (
                    <img 
                      src={item.thumbnailUrl} 
                      alt={`Video-Vorschaubild ${index + 1}`}
                      loading="lazy"
                    />
                  ) : item.isVideo ? (
                    // For videos, use displayUrl (processedThumbnailUrl) or thumbnailUrl or placeholder
                    (item.displayUrl || item.processedThumbnailUrl || item.thumbnailUrl) ? (
                      <img 
                        src={item.displayUrl || item.processedThumbnailUrl || item.thumbnailUrl} 
                        alt={`Video-Vorschaubild ${index + 1}`}
                        loading="lazy"
                      />
                    ) : (
                      <div className={classes.videoPlaceholder}>
                        <span>🎥</span>
                        <p>Video</p>
                      </div>
                    )
                  ) : item.displayUrl ? (
                    // For photos, use displayUrl
                    <img 
                      src={item.displayUrl} 
                      alt={`${project.title} ${index + 1}`}
                      loading="lazy"
                    />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={classes.phoneContainer}>
        <div 
            className={classes.imageCarousel} 
            ref={carouselRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ touchAction: 'pan-x', overflow: 'hidden', cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            {mediaItems?.length > 0 && (
              <>
                <div 
                  style={{ 
                    display: 'flex', 
                    width: '100%', 
                    height: '100%', 
                    transform: `translateX(${translateX}%)`, 
                    transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                    justifyContent: 'flex-start'

                  }}
                >
                  {mediaItems?.length > 0 && mediaItems.map((item, index) => (
                    <div 
                      key={`slide-${index}`} 
                      style={{ minWidth: '100%', height: '100%', position: 'relative', cursor: 'pointer' }}
                      onClick={(e) => {
                        // Only open carousel if not dragging
                        if (!hasDragged) {
                          handleGalleryItemClick(index);
                        }
                      }}
                    >
                      {item.isLink && (
                        <div className={classes.linkBadge}>
                          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="40" cy="40" r="40" fill="rgba(0, 0, 0, 0.6)"/>
                            <path d="M32 24L56 40L32 56V24Z" fill="white"/>
                          </svg>
                        </div>
                      )}
                      {item.isVideo && !item.isLink && (
                        <div className={classes.videoBadge}>
                          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="40" cy="40" r="40" fill="rgba(0, 0, 0, 0.6)"/>
                            <path d="M32 24L56 40L32 56V24Z" fill="white"/>
                          </svg>
                        </div>
                      )}
                      {item.isLink ? (
                        item.thumbnailUrl ? (
                          <img
                            src={item.thumbnailUrl}
                            alt={`Video-Vorschaubild ${index + 1}`}
                            loading={index === 0 ? 'eager' : 'lazy'}
                            decoding="async"
                            fetchpriority={index === 0 ? 'high' : 'auto'}
                          />
                        ) : (
                          <div className={classes.linkPlaceholder}>
                            <span>🔗</span>
                            <p>Link</p>
                          </div>
                        )
                      ) : item.isVideo ? (
                        (item.displayUrl || item.processedThumbnailUrl || item.thumbnailUrl) ? (
                          <img
                            src={item.displayUrl || item.processedThumbnailUrl || item.thumbnailUrl}
                            alt={`Video-Vorschaubild ${index + 1}`}
                            loading={index === 0 ? 'eager' : 'lazy'}
                            decoding="async"
                            fetchpriority={index === 0 ? 'high' : 'auto'}
                          />
                        ) : (
                          <div className={classes.videoPlaceholder}>
                            <span>🎥</span>
                            <p>Video</p>
                          </div>
                        )
                      ) : item.displayUrl ? (
                        <img
                          src={item.displayUrl}
                          alt={`${project.title} ${index + 1}`}
                          loading={index === 0 ? 'eager' : 'lazy'}
                          decoding="async"
                          fetchpriority={index === 0 ? 'high' : 'auto'}
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
                <div className={`${classes.imageCounter} slimRegularMiniText`}>
                  {currentImageIndex + 1} / {mediaItems?.length}
                </div>
                <button onClick={handleClose} className={classes.closeButtonPhone}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="white"/>
                  </svg>
                </button>
              </>
            )}
        </div>
        <div className={classes.phoneMainContent}>
            <h2 className="titleSemibold3">{project?.title}</h2>
            <div className={classes.phoneMainContentDescription}>
              <h3 className="semiboldLarge">Projektbeschreibung</h3>
              <p className="regularLarge">{project?.description}</p>
            </div>
        </div>
        {!isMemberPage && isOwner && creativeId && projectId && (
          <div className={classes.phoneFooterContainer} style={fixedPhoneFooterStyle}>
            <Link to={`/creative-editing/${creativeId}/project/${projectId}`} className={classes.phoneFooterButton}>
              <span className="mediumLarge">Bearbeiten</span>
            </Link>
          </div>
        )}
      </div>

      {carouselOpen && mediaItems?.length > 0 && (
        <ProjectCarousel
          mediaItems={mediaItems}
          startIndex={carouselStartIndex}
          onClose={handleCarouselClose}
        />
      )}
    </div>
  );
};

export default ProjectOverview;
